/**
 * Dashboard — authenticated landing page.
 *
 * Per `rcp-dashboard.html`:
 *   - Tile grid (applications, inbox unread, next appointment, open docs).
 *   - Applications list card.
 *   - Next appointment card.
 *   - Quick-actions stack.
 *
 * A11y fixes vs mockup:
 *   - Tile DIVs → `<button>` (mockup #1 risk).
 *   - Inbox badge: not color-only — includes "unread" text via visually-hidden.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type {
  AppointmentDto,
  NotificationLogDto,
  StatusPeekDto,
} from '@retail-ops/shared-types';
import { getApiClient } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/a11y/AccessibleToast';
import { Inbox as InboxIcon, FileText, Calendar, UserCheck, ChevronRight } from '@retail-ops/panel-shell/fi';

export function Dashboard(): JSX.Element {
  const { identity } = useAuth();
  const { pushToast } = useToast();
  const [inbox, setInbox] = useState<ReadonlyArray<NotificationLogDto>>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [appointments, setAppointments] = useState<ReadonlyArray<AppointmentDto>>([]);
  const [status, setStatus] = useState<StatusPeekDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const client = getApiClient();
      try {
        // TODO(PLANNED /portal/inbox + /portal/appointments + /portal/status/*):
        // all three calls depend on routes that are still planned in the Function.
        const [inboxRes, apptRes] = await Promise.all([
          client.portal.inbox.list({ top: 5, signal: controller.signal }),
          client.portal.appointments.list({ signal: controller.signal }),
        ]);
        if (inboxRes.success && inboxRes.data) {
          setInbox(inboxRes.data);
          setUnreadCount(inboxRes.data.filter((n) => !n.readAt).length);
        }
        if (apptRes.success && apptRes.data) setAppointments(apptRes.data);
      } catch (e) {
        if ((e as Error)?.name !== 'AbortError') {
          pushToast('error', 'Could not load your dashboard', 'Please refresh the page.');
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [pushToast]);

  const nextAppt = appointments[0];

  return (
    <>
      <section aria-labelledby="welcome-heading" style={{ marginBottom: 24 }}>
        <h1 id="welcome-heading" tabIndex={-1}>
          Welcome{identity?.displayName ? `, ${identity.displayName}` : ''}.
        </h1>
        <p style={{ color: 'var(--ink-500)', fontSize: 15 }}>
          Here&apos;s a quick look at what&apos;s happening with your accounts.
        </p>
      </section>

      <section aria-label="Summary" className="tile-grid">
        <Link to="/dashboard" className="tile" aria-label="Applications in progress">
          <div className="tile-icon"><FileText aria-hidden="true" size={22} /></div>
          <div className="tile-label">Applications</div>
          <div className="tile-value">{status ? 1 : 0}</div>
          <div className="tile-sub">in progress</div>
        </Link>
        <Link
          to="/inbox"
          className={`tile${unreadCount > 0 ? ' tile-urgent' : ''}`}
          aria-label={`Inbox, ${unreadCount} unread message${unreadCount === 1 ? '' : 's'}`}
        >
          <div className="tile-icon"><InboxIcon aria-hidden="true" size={22} /></div>
          <div className="tile-label">Inbox</div>
          <div className="tile-value">
            {unreadCount}
            <span className="visually-hidden"> unread</span>
          </div>
          <div className="tile-sub">{unreadCount === 0 ? 'no new messages' : 'needs your attention'}</div>
        </Link>
        <Link to="/appointment/view" className="tile" aria-label="Upcoming appointments">
          <div className="tile-icon"><Calendar aria-hidden="true" size={22} /></div>
          <div className="tile-label">Next appointment</div>
          <div className="tile-value">{appointments.length}</div>
          <div className="tile-sub">{nextAppt ? 'scheduled' : 'none scheduled'}</div>
        </Link>
        <Link to="/preferences" className="tile" aria-label="Preferences">
          <div className="tile-icon"><UserCheck aria-hidden="true" size={22} /></div>
          <div className="tile-label">Preferences</div>
          <div className="tile-value">&middot;&middot;&middot;</div>
          <div className="tile-sub">notification settings</div>
        </Link>
      </section>

      <section className="section-grid">
        <div>
          <div className="card">
            <h2 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Your applications</span>
              <Link to="/apply/start" style={{ fontSize: 13, fontWeight: 400 }}>
                Start a new application
              </Link>
            </h2>
            {loading ? (
              <p aria-live="polite">Loading...</p>
            ) : status ? (
              <Link
                to={`/status/${status.cartId}`}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, border: '1px solid var(--surface-border)', borderRadius: 8 }}
              >
                <span>
                  <strong>{status.currentStage ?? status.status}</strong>
                  {status.nextAction ? <div style={{ fontSize: 13, color: 'var(--ink-500)' }}>{status.nextAction}</div> : null}
                </span>
                <ChevronRight aria-hidden="true" size={18} />
              </Link>
            ) : (
              <p style={{ color: 'var(--ink-500)' }}>
                You don&apos;t have any applications in progress. <Link to="/apply/start">Apply now</Link>.
              </p>
            )}
          </div>

          <div className="card">
            <h2 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Recent messages</span>
              <Link to="/inbox" style={{ fontSize: 13, fontWeight: 400 }}>See all</Link>
            </h2>
            {inbox.length === 0 ? (
              <p style={{ color: 'var(--ink-500)' }}>No messages yet.</p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {inbox.slice(0, 3).map((n) => (
                  <li key={n.notificationId} style={{ padding: '12px 0', borderBottom: '1px solid var(--surface-border)' }}>
                    <div style={{ fontWeight: 500, color: 'var(--pcb-blue-dark)' }}>{n.subject ?? n.category}</div>
                    <div style={{ fontSize: 13, color: 'var(--ink-500)' }}>{n.sentAt}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <aside>
          <div className="card">
            <h2>Next appointment</h2>
            {nextAppt ? (
              <div>
                <time dateTime={nextAppt.startsAt} style={{ fontWeight: 500, color: 'var(--pcb-blue-dark)' }}>
                  {new Date(nextAppt.startsAt).toLocaleString()}
                </time>
                <div style={{ fontSize: 13, color: 'var(--ink-500)', marginTop: 4 }}>
                  {nextAppt.serviceName ?? 'Appointment'} &middot; {nextAppt.branch ?? 'Branch TBD'}
                </div>
                <div style={{ marginTop: 12 }}>
                  <Link to={`/appointment/view?id=${nextAppt.appointmentId}`} className="btn btn-secondary">
                    View details
                  </Link>
                </div>
              </div>
            ) : (
              <p style={{ color: 'var(--ink-500)' }}>Nothing scheduled.</p>
            )}
          </div>
          <div className="card">
            <h2>Quick actions</h2>
            <div style={{ display: 'grid', gap: 8 }}>
              <Link to="/apply/start" className="btn btn-ghost">Open a new account</Link>
              <Link to="/appointment/view" className="btn btn-ghost">Manage appointment</Link>
              <Link to="/preferences" className="btn btn-ghost">Notification preferences</Link>
            </div>
          </div>
        </aside>
      </section>
    </>
  );
}
